class CustomTestimonials extends HTMLElement {

  connectedCallback() {

    if (
      this.dataset.initialized ===
      'true'
    ) {
      return;
    }


    this.dataset.initialized =
      'true';


    /* ========================================================
       ELEMENTS
       ======================================================== */

    this.stage =
      this.querySelector(
        '[data-testimonial-stage]'
      );


    this.panelsContainer =
      this.querySelector(
        '[data-testimonial-panels]'
      );


    this.panels =
      [
        ...this.querySelectorAll(
          '[data-testimonial-panel]'
        )
      ];


    this.progressContainer =
      this.querySelector(
        '[data-testimonial-progress]'
      );


    this.previousButton =
      this.querySelector(
        '[data-testimonial-prev]'
      );


    this.nextButton =
      this.querySelector(
        '[data-testimonial-next]'
      );


    this.shell =
      this.querySelector(
        '[data-testimonial-shell]'
      );


    this.emptyState =
      this.querySelector(
        '[data-testimonial-empty]'
      );


    /* ========================================================
       STATE
       ======================================================== */

    this.total =
      this.panels.length;


    this.activeIndex =
      0;


    this.transitioning =
      false;


    this.autoplayEnabled =
      this.dataset.autoplay ===
      'true';


    this.autoplayInterval =
      Math.max(
        3000,
        Number(
          this.dataset.autoplayInterval ||
          6000
        )
      );


    this.autoplayTimer =
      null;


    this.resizeTimer =
      null;


    this.progressSegments =
      [];


    this.touchStartX =
      null;


    this.touchStartY =
      null;


    this.isVisible =
      true;


    this.hasFocus =
      false;


    this.collapsedPanelHeight =
      0;


    /* ========================================================
       BIND
       ======================================================== */

    this.handlePrevious =
      this.handlePrevious.bind(this);


    this.handleNext =
      this.handleNext.bind(this);


    this.handleKeydown =
      this.handleKeydown.bind(this);


    this.handleTouchStart =
      this.handleTouchStart.bind(this);


    this.handleTouchEnd =
      this.handleTouchEnd.bind(this);


    this.handleReadMore =
      this.handleReadMore.bind(this);


    this.handleFocusIn =
      this.handleFocusIn.bind(this);


    this.handleFocusOut =
      this.handleFocusOut.bind(this);


    this.handleVisibilityChange =
      this.handleVisibilityChange.bind(this);


    this.handleResize =
      this.handleResize.bind(this);


    this.initialize();
  }



  /* ============================================================
     INITIALIZE
     ============================================================ */

  initialize() {

    if (
      !this.total
    ) {

      if (this.shell) {
        this.shell.hidden =
          true;
      }


      if (this.emptyState) {
        this.emptyState.hidden =
          false;
      }


      return;
    }


    this.style.setProperty(
      '--custom-testimonial-count',
      String(
        this.total
      )
    );


    this.createProgressSegments();


    this.addEventListeners();


    this.setupIntersectionObserver();


    this.setPanelStates(
      0
    );


    requestAnimationFrame(
      () => {

        requestAnimationFrame(
          () => {

            this.initializeReadMore();

            this.measureCollapsedHeight();

            this.applyCollapsedHeight();

            this.restartAutoplay();

          }
        );

      }
    );


    if (
      document.fonts?.ready
    ) {

      document.fonts.ready
        .then(
          () => {

            this.initializeReadMore();

            this.measureCollapsedHeight();

            this.applyCollapsedHeight();

          }
        )
        .catch(
          () => {}
        );

    }
  }



  /* ============================================================
     EVENTS
     ============================================================ */

  addEventListeners() {

    this.previousButton
      ?.addEventListener(
        'click',
        this.handlePrevious
      );


    this.nextButton
      ?.addEventListener(
        'click',
        this.handleNext
      );


    this.stage
      ?.addEventListener(
        'keydown',
        this.handleKeydown
      );


    this.stage
      ?.addEventListener(
        'touchstart',
        this.handleTouchStart,
        {
          passive:
            true
        }
      );


    this.stage
      ?.addEventListener(
        'touchend',
        this.handleTouchEnd,
        {
          passive:
            true
        }
      );


    this.addEventListener(
      'click',
      this.handleReadMore
    );


    this.addEventListener(
      'focusin',
      this.handleFocusIn
    );


    this.addEventListener(
      'focusout',
      this.handleFocusOut
    );


    document.addEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );


    window.addEventListener(
      'resize',
      this.handleResize
    );
  }



  /* ============================================================
     PROGRESS
     ============================================================ */

  createProgressSegments() {

    if (
      !this.progressContainer
    ) {
      return;
    }


    this.progressContainer.innerHTML =
      '';


    this.progressSegments =
      [];


    for (
      let index = 0;
      index < this.total;
      index += 1
    ) {

      const button =
        document.createElement(
          'button'
        );


      button.type =
        'button';


      button.className =
        'custom-testimonials__progress-segment';


      button.setAttribute(
        'aria-label',
        `Show testimonial ${index + 1}`
      );


      const track =
        document.createElement(
          'span'
        );


      track.className =
        'custom-testimonials__progress-track';


      const fill =
        document.createElement(
          'span'
        );


      fill.className =
        'custom-testimonials__progress-fill';


      track.appendChild(
        fill
      );


      button.appendChild(
        track
      );


      button.addEventListener(
        'click',
        () => {

          this.goTo(
            index,
            true
          );

        }
      );


      this.progressContainer
        .appendChild(
          button
        );


      this.progressSegments
        .push(
          button
        );
    }


    this.updateProgress(
      this.activeIndex
    );
  }



  /* ============================================================
     CIRCULAR INDEX
     ============================================================ */

  normalizeIndex(
    index
  ) {

    return (
      (
        index %
        this.total
      ) +
      this.total
    ) %
    this.total;
  }



  /* ============================================================
     GHOST POSITIONING
     ============================================================ */

  setPanelStates(
    activeIndex
  ) {

    const previous =
      this.normalizeIndex(
        activeIndex - 1
      );


    const next =
      this.normalizeIndex(
        activeIndex + 1
      );


    const previousFar =
      this.normalizeIndex(
        activeIndex - 2
      );


    const nextFar =
      this.normalizeIndex(
        activeIndex + 2
      );


    this.panels.forEach(
      (
        panel,
        index
      ) => {

        panel.classList.remove(
          'is-active',
          'is-ghost-prev',
          'is-ghost-next',
          'is-ghost-prev-far',
          'is-ghost-next-far'
        );


        let visible =
          false;


        if (
          index ===
          activeIndex
        ) {

          panel.classList.add(
            'is-active'
          );


          visible =
            true;

        } else if (
          this.total > 1 &&
          index === previous
        ) {

          panel.classList.add(
            'is-ghost-prev'
          );


          visible =
            true;

        } else if (
          this.total > 2 &&
          index === next
        ) {

          panel.classList.add(
            'is-ghost-next'
          );


          visible =
            true;

        } else if (
          this.total > 4 &&
          index === previousFar
        ) {

          panel.classList.add(
            'is-ghost-prev-far'
          );


          visible =
            true;

        } else if (
          this.total > 4 &&
          index === nextFar
        ) {

          panel.classList.add(
            'is-ghost-next-far'
          );


          visible =
            true;
        }


        if (
          index ===
          activeIndex
        ) {

          panel.removeAttribute(
            'aria-hidden'
          );


          panel.inert =
            false;

        } else {

          panel.setAttribute(
            'aria-hidden',
            'true'
          );


          panel.inert =
            true;
        }


        panel.style.visibility =
          visible
            ? ''
            : 'hidden';
      }
    );


    this.updateProgress(
      activeIndex
    );
  }



  /* ============================================================
     CHANGE REVIEW
     ============================================================ */

  goTo(
    index,
    userInitiated = false
  ) {

    if (
      this.transitioning ||
      this.total <= 1
    ) {
      return;
    }


    index =
      this.normalizeIndex(
        index
      );


    if (
      index ===
      this.activeIndex
    ) {

      if (
        userInitiated
      ) {
        this.restartAutoplay();
      }


      return;
    }


    this.transitioning =
      true;


    /*
     * ALWAYS collapse currently expanded content before
     * another slide becomes active.
     */

    this.collapseAllReviews();


    this.applyCollapsedHeight();


    this.clearAutoplay();


    this.activeIndex =
      index;


    this.setPanelStates(
      index
    );


    window.setTimeout(
      () => {

        this.transitioning =
          false;


        this.restartAutoplay();

      },
      500
    );
  }



  /* ============================================================
     PREVIOUS / NEXT
     ============================================================ */

  handlePrevious() {

    this.goTo(
      this.activeIndex - 1,
      true
    );
  }


  handleNext() {

    this.goTo(
      this.activeIndex + 1,
      true
    );
  }



  /* ============================================================
     KEYBOARD
     ============================================================ */

  handleKeydown(
    event
  ) {

    if (
      event.target.closest(
        'button, a, input, select, textarea'
      )
    ) {
      return;
    }


    if (
      event.key ===
      'ArrowLeft'
    ) {

      event.preventDefault();

      this.handlePrevious();

    }


    if (
      event.key ===
      'ArrowRight'
    ) {

      event.preventDefault();

      this.handleNext();

    }
  }



  /* ============================================================
     TOUCH / SWIPE
     ============================================================ */

  handleTouchStart(
    event
  ) {

    const touch =
      event.changedTouches?.[0];


    if (!touch) {
      return;
    }


    this.touchStartX =
      touch.clientX;


    this.touchStartY =
      touch.clientY;
  }


  handleTouchEnd(
    event
  ) {

    if (
      this.touchStartX === null ||
      this.touchStartY === null
    ) {
      return;
    }


    const touch =
      event.changedTouches?.[0];


    if (!touch) {
      return;
    }


    const deltaX =
      touch.clientX -
      this.touchStartX;


    const deltaY =
      touch.clientY -
      this.touchStartY;


    this.touchStartX =
      null;


    this.touchStartY =
      null;


    /*
     * Normal vertical scrolling should not trigger slides.
     */

    if (
      Math.abs(deltaY) >
      Math.abs(deltaX)
    ) {
      return;
    }


    if (
      Math.abs(deltaX) <
      45
    ) {
      return;
    }


    if (
      deltaX < 0
    ) {

      this.handleNext();

    } else {

      this.handlePrevious();

    }
  }



  /* ============================================================
     PROGRESS STATE
     ============================================================ */

  updateProgress(
    activeIndex
  ) {

    this.progressSegments.forEach(
      (
        segment,
        index
      ) => {

        segment.classList.remove(
          'is-active'
        );


        segment.removeAttribute(
          'aria-current'
        );


        if (
          index ===
          activeIndex
        ) {

          /*
           * Force reflow so autoplay animation restarts.
           */

          void segment.offsetWidth;


          segment.classList.add(
            'is-active'
          );


          segment.setAttribute(
            'aria-current',
            'true'
          );

        }

      }
    );
  }



  /* ============================================================
     READ MORE DETECTION
     ============================================================ */

  initializeReadMore() {

    this.panels.forEach(
      panel => {

        const body =
          panel.querySelector(
            '[data-testimonial-body]'
          );


        const button =
          panel.querySelector(
            '[data-testimonial-read-more]'
          );


        const wrapper =
          panel.querySelector(
            '[data-testimonial-body-wrapper]'
          );


        if (
          !body ||
          !button ||
          !wrapper
        ) {
          return;
        }


        wrapper.classList.remove(
          'is-expanded'
        );


        button.hidden =
          true;


        const originalVisibility =
          panel.style.visibility;


        panel.style.visibility =
          'hidden';


        panel.classList.add(
          'is-active'
        );


        /*
         * Temporarily use normal active styling so line clamp
         * can be measured properly.
         */

        const overflow =
          body.scrollHeight >
          body.clientHeight + 2;


        button.hidden =
          !overflow;


        panel.classList.remove(
          'is-active'
        );


        panel.style.visibility =
          originalVisibility;
      }
    );


    /*
     * Restore real carousel state after measurement.
     */

    this.setPanelStates(
      this.activeIndex
    );
  }



  /* ============================================================
     READ MORE
     ============================================================ */

  handleReadMore(
    event
  ) {

    const button =
      event.target.closest(
        '[data-testimonial-read-more]'
      );


    if (!button) {
      return;
    }


    const panel =
      button.closest(
        '[data-testimonial-panel]'
      );


    if (
      !panel ||
      !panel.classList.contains(
        'is-active'
      )
    ) {
      return;
    }


    const wrapper =
      button.closest(
        '[data-testimonial-body-wrapper]'
      );


    if (!wrapper) {
      return;
    }


    const expanded =
      wrapper.classList.toggle(
        'is-expanded'
      );


    button.setAttribute(
      'aria-expanded',
      String(
        expanded
      )
    );


    button.textContent =
      expanded
        ? 'Read less'
        : 'Read more';


    /*
     * Stop autoplay while user is reading expanded copy.
     */

    if (expanded) {

      this.clearAutoplay();


      this.applyExpandedHeight(
        panel
      );

    } else {

      this.applyCollapsedHeight();


      this.restartAutoplay();

    }
  }



  /* ============================================================
     COLLAPSE
     ============================================================ */

  collapseAllReviews() {

    this.panels.forEach(
      panel => {

        const wrapper =
          panel.querySelector(
            '[data-testimonial-body-wrapper]'
          );


        const button =
          panel.querySelector(
            '[data-testimonial-read-more]'
          );


        wrapper?.classList.remove(
          'is-expanded'
        );


        if (button) {

          button.setAttribute(
            'aria-expanded',
            'false'
          );


          button.textContent =
            'Read more';

        }

      }
    );

    this.stage?.classList.remove(
      'has-expanded-review'
    );
  }



  /* ============================================================
     COLLAPSED HEIGHT

     Measure the tallest *collapsed* review.
     This stabilizes carousel height without allowing long
     reviews to make the section enormous.
     ============================================================ */

  measureCollapsedHeight() {

    if (
      !this.panelsContainer
    ) {
      return;
    }


    this.collapseAllReviews();


    let maximum =
      0;


    this.panels.forEach(
      panel => {

        const originalVisibility =
          panel.style.visibility;


        const originalPosition =
          panel.style.position;


        const originalTransform =
          panel.style.transform;


        const originalOpacity =
          panel.style.opacity;


        panel.style.visibility =
          'hidden';


        panel.style.position =
          'relative';


        panel.style.transform =
          'none';


        panel.style.opacity =
          '1';


        panel.classList.add(
          'is-active'
        );


        const height =
          panel.scrollHeight;


        maximum =
          Math.max(
            maximum,
            height
          );


        panel.classList.remove(
          'is-active'
        );


        panel.style.visibility =
          originalVisibility;


        panel.style.position =
          originalPosition;


        panel.style.transform =
          originalTransform;


        panel.style.opacity =
          originalOpacity;
      }
    );


    this.collapsedPanelHeight =
      Math.ceil(
        maximum
      );


    this.setPanelStates(
      this.activeIndex
    );
  }



  /* ============================================================
     APPLY COLLAPSED HEIGHT
     ============================================================ */

  applyCollapsedHeight() {
    if (
      !this.panelsContainer ||
      !this.collapsedPanelHeight
    ) {
      return;
    }


    this.panelsContainer.style.setProperty(
      '--custom-testimonial-panel-height',
      `${this.collapsedPanelHeight}px`
    );


    /*
    * Keep the complete testimonial stage compact.
    *
    * CSS controls the actual responsive stage height
    * rather than allowing content measurement to create
    * an excessively tall desktop section.
    */
    this.stage?.style.removeProperty(
      '--custom-testimonial-stage-height'
    );
  }



  /* ============================================================
     EXPANDED HEIGHT
     ============================================================ */

  applyExpandedHeight(panel) {
    if (
      !panel ||
      !this.panelsContainer
    ) {
      return;
    }


    const height =
      panel.scrollHeight;


    this.panelsContainer.style.setProperty(
      '--custom-testimonial-panel-height',
      `${Math.ceil(height)}px`
    );


    /*
    * Expanded review may grow naturally.
    * Do not permanently redefine the stage height.
    */
    this.stage?.classList.add(
      'has-expanded-review'
    );
  }



  /* ============================================================
     AUTOPLAY
     ============================================================ */

  restartAutoplay() {

    this.clearAutoplay();


    this.updateProgress(
      this.activeIndex
    );


    this.scheduleAutoplay();
  }


  scheduleAutoplay() {

    if (
      !this.canAutoplay()
    ) {
      return;
    }


    this.autoplayTimer =
      window.setTimeout(
        () => {

          this.goTo(
            this.activeIndex + 1,
            false
          );

        },
        this.autoplayInterval
      );
  }


  clearAutoplay() {

    if (
      this.autoplayTimer
    ) {

      window.clearTimeout(
        this.autoplayTimer
      );


      this.autoplayTimer =
        null;

    }
  }


  canAutoplay() {

    const activePanel =
      this.panels[
        this.activeIndex
      ];


    const expanded =
      activePanel
        ?.querySelector(
          '[data-testimonial-body-wrapper]'
        )
        ?.classList.contains(
          'is-expanded'
        );


    return (
      this.autoplayEnabled &&
      this.total > 1 &&
      this.isVisible &&
      !document.hidden &&
      !this.hasFocus &&
      !this.transitioning &&
      !expanded
    );
  }



  /* ============================================================
     FOCUS
     ============================================================ */

  handleFocusIn() {

    this.hasFocus =
      true;


    this.clearAutoplay();
  }


  handleFocusOut() {

    requestAnimationFrame(
      () => {

        this.hasFocus =
          this.contains(
            document.activeElement
          );


        if (
          !this.hasFocus
        ) {

          this.restartAutoplay();

        }

      }
    );
  }



  /* ============================================================
     VISIBILITY
     ============================================================ */

  handleVisibilityChange() {

    if (
      document.hidden
    ) {

      this.clearAutoplay();

    } else {

      this.restartAutoplay();

    }
  }



  /* ============================================================
     VIEWPORT
     ============================================================ */

  setupIntersectionObserver() {

    if (
      !(
        'IntersectionObserver'
        in window
      )
    ) {
      return;
    }


    this.intersectionObserver =
      new IntersectionObserver(
        entries => {

          const entry =
            entries[0];


          if (!entry) {
            return;
          }


          this.isVisible =
            entry.isIntersecting &&
            entry.intersectionRatio >
            0.12;


          if (
            this.isVisible
          ) {

            this.restartAutoplay();

          } else {

            this.clearAutoplay();

          }

        },
        {
          threshold: [
            0,
            0.12,
            0.5
          ]
        }
      );


    this.intersectionObserver
      .observe(
        this
      );
  }



  /* ============================================================
     RESIZE
     ============================================================ */

  handleResize() {

    clearTimeout(
      this.resizeTimer
    );


    this.resizeTimer =
      window.setTimeout(
        () => {

          this.collapseAllReviews();

          this.initializeReadMore();

          this.measureCollapsedHeight();

          this.applyCollapsedHeight();

          this.restartAutoplay();

        },
        180
      );
  }



  /* ============================================================
     CLEANUP
     ============================================================ */

  disconnectedCallback() {

    this.clearAutoplay();


    clearTimeout(
      this.resizeTimer
    );


    this.intersectionObserver
      ?.disconnect();


    this.previousButton
      ?.removeEventListener(
        'click',
        this.handlePrevious
      );


    this.nextButton
      ?.removeEventListener(
        'click',
        this.handleNext
      );


    this.stage
      ?.removeEventListener(
        'keydown',
        this.handleKeydown
      );


    this.stage
      ?.removeEventListener(
        'touchstart',
        this.handleTouchStart
      );


    this.stage
      ?.removeEventListener(
        'touchend',
        this.handleTouchEnd
      );


    this.removeEventListener(
      'click',
      this.handleReadMore
    );


    this.removeEventListener(
      'focusin',
      this.handleFocusIn
    );


    this.removeEventListener(
      'focusout',
      this.handleFocusOut
    );


    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );


    window.removeEventListener(
      'resize',
      this.handleResize
    );
  }

}


if (
  !customElements.get(
    'custom-testimonials'
  )
) {

  customElements.define(
    'custom-testimonials',
    CustomTestimonials
  );

}